// src/@types/property.ts
/**
 * Property Video Generator Types
 * Feature: AI-powered property marketing video generation
 */

import type { ReactNode } from 'react';
import type { Id } from '../../convex/_generated/dataModel';

// ============================================================================
// Property Listing Types
// ============================================================================

export interface IPropertyCoordinates {
  lat: number;
  lng: number;
}

export interface INearbyPlace {
  name: string;
  distance: number; // meters
}

export interface INearbyPlaces {
  schools: INearbyPlace[];
  kindergartens: INearbyPlace[];
  supermarkets: INearbyPlace[];
  restaurants: INearbyPlace[];
  busStops: INearbyPlace[];
  pharmacies: INearbyPlace[];
}

export type PropertyListingStatus =
  | 'scraping'
  | 'analyzing_location'
  | 'generating_description'
  | 'agent_pending'
  | 'ready'
  | 'failed';

export interface IPropertyListing {
  _id: Id<'propertyListings'>;
  _creationTime: number;
  userId: Id<'users'>;
  sourceUrl: string;
  sourceDomain: string;
  scrapedAt: number;
  createdAt: number;
  title: string;
  price?: number;
  currency?: string;
  propertyType?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareMeters?: number;
  yearBuilt?: number;
  features?: string[];
  rawDescription?: string;
  characteristics?: Record<string, string>;
  plotArea?: number;
  condition?: string;
  parking?: string;
  postalCode?: string;
  energyEfficiency?: string;
  referenceNumber?: string;
  address?: string;
  city?: string;
  coordinates?: IPropertyCoordinates;
  imageIds: Id<'_storage'>[];
  nearbyPlaces?: INearbyPlaces;
  generatedDescription?: string;
  status: PropertyListingStatus;
  errorMessage?: string;
}

// ============================================================================
// Property Video Types
// ============================================================================

export type PropertyVideoStatus =
  | 'pending'
  | 'generating_scenes'
  | 'generating_voiceovers'
  | 'generating_videos'
  | 'muxing'
  | 'ready_for_muxing'
  | 'completed'
  | 'failed';

// ============================================================================
// Property Scene Types
// ============================================================================

export type PropertySceneType =
  // Special video structure types
  | 'intro'
  | 'feature'
  | 'neighborhood'
  | 'outro'
  // Property area types
  | 'building' // Building facade/front view - uses Day-Night templates
  | 'backyard'
  | 'pool'
  | 'garden'
  | 'parking'
  | 'garage'
  | 'living_room'
  | 'kitchen'
  | 'bedroom'
  | 'bathroom'
  | 'balcony'
  | 'plan' // Floor plan
  | 'other'
  // Deprecated types (for backwards compatibility)
  | 'exterior' // @deprecated - use 'building' instead
  | 'interior'; // @deprecated - use specific room type instead

export type PropertySceneStatus =
  | 'pending'
  | 'voiceover_generating'
  | 'video_generating'
  | 'completed'
  | 'failed';

export interface IPropertyScene {
  _id: Id<'propertyScenes'>;
  _creationTime: number;
  propertyVideoId: Id<'propertyVideos'>;
  sceneNumber: number;
  sceneType: PropertySceneType;
  sourceImageId?: Id<'_storage'>;
  selectedImageId?: Id<'_storage'>;
  sceneDescription: string;
  voiceoverText: string;
  voiceoverAudioId?: Id<'_storage'>;
  voiceoverDuration?: number;
  generatedVideoId?: Id<'_storage'>;
  imageToVideoPrompt?: string;
  imageToVideoStatus?: 'pending' | 'generating' | 'completed' | 'failed';
  status: PropertySceneStatus;
  // User-edited fields (override AI-generated)
  editedVoiceoverText?: string;
  editedImagePrompt?: string;
  // Muxed video fields (video + voiceover combined)
  muxedVideoStorageId?: Id<'_storage'>;
  muxedStatus?: PropertySceneMuxStatus;
  muxedAt?: number;
  muxedFileSizeBytes?: number;
  muxedErrorMessage?: string;
  muxedWithVoiceoverAudioId?: Id<'_storage'>;
  // Prompt template fields (can be system or user template)
  promptTemplateId?: Id<'promptTemplates'> | Id<'userPromptTemplates'>;
  editedNegativePrompt?: string;
}

// ============================================================================
// Property Scene Muxing Types
// ============================================================================

/**
 * Muxing status for property scenes
 */
export type PropertySceneMuxStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * State for property scene muxer hook
 */
export interface IPropertySceneMuxerState {
  status: 'idle' | 'downloading' | 'muxing' | 'uploading' | 'completed' | 'error';
  progress: number;
  error: string | null;
  resultStorageId: Id<'_storage'> | null;
}

/**
 * Return type for usePropertySceneMuxer hook
 */
export interface IUsePropertySceneMuxerReturn {
  muxPropertyScene: (
    sceneId: Id<'propertyScenes'>,
    videoUrl: string,
    audioUrl: string
  ) => Promise<void>;
  state: IPropertySceneMuxerState;
  abort: () => void;
  reset: () => void;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface IPropertyUrlInputProps {
  onSubmit: (url: string) => void | Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  placeholder?: string;
}

export interface IPropertyDataReviewProps {
  property: IPropertyListing;
  onEdit?: (field: string, value: string | number) => void;
  /** When true, shows nearby places section in the accordion (for draft flow) */
  showNearbyPlaces?: boolean;
}

/**
 * Scene status summary for tracking progress
 */
export interface ISceneStatusSummary {
  /** Number of scenes pending */
  pendingCount: number;
  /** Number of scenes with voiceover generating */
  voiceoverGeneratingCount: number;
  /** Number of scenes with video generating */
  videoGeneratingCount: number;
  /** Number of completed scenes */
  completedCount: number;
  /** Number of failed scenes */
  failedCount: number;
}

export interface IPropertyNearbyPlacesProps {
  nearbyPlaces: IPropertyListing['nearbyPlaces'];
}

// ============================================================================
// Video Settings Types
// ============================================================================

/**
 * Wizard step type (1-6)
 */
export type PropertyVideoWizardStep = 1 | 2 | 3 | 4 | 5 | 6;

// ============================================================================
// PropertyDataReview Helper Component Props
// ============================================================================

/**
 * Props for data field display helper component
 */
export interface IDataFieldProps {
  label: string;
  value: string | number | undefined;
  icon?: ReactNode;
}

// ============================================================================
// PropertyNearbyPlaces Helper Component Props
// ============================================================================

/**
 * Category configuration with icon and label for nearby places
 */
export interface ICategoryConfig {
  key: keyof INearbyPlaces;
  label: string;
  icon: ReactNode;
}

/**
 * Props for individual place item display
 */
export interface IPlaceItemProps {
  place: INearbyPlace;
}

/**
 * Props for category card display
 */
export interface ICategoryCardProps {
  config: ICategoryConfig;
  places: INearbyPlace[];
}

// ============================================================================
// PropertyVideoWizardComponents Props
// ============================================================================

/**
 * Props for scraping loading state component
 */
export interface IScrapingLoadingStateProps {
  status: string;
}

/**
 * Props for error display component
 */
export interface IErrorDisplayProps {
  message: string;
  onRetry: () => void;
}

// ============================================================================
// PropertyDataReview Section Component Props
// ============================================================================

/**
 * Props for BasicInfoSection component
 */
export interface IBasicInfoSectionProps {
  title: string;
  propertyType?: string;
  price?: number;
  currency?: string;
}

/**
 * Props for SpecificationsSection component
 */
export interface ISpecificationsSectionProps {
  bedrooms?: number;
  bathrooms?: number;
  squareMeters?: number;
  yearBuilt?: number;
}

/**
 * Props for LocationSection component
 */
export interface ILocationSectionProps {
  address?: string;
  city?: string;
  coordinates?: IPropertyCoordinates;
}

/**
 * Props for FeaturesSection component
 */
export interface IFeaturesSectionProps {
  features?: string[];
}

/**
 * Props for DescriptionSection component
 */
export interface IDescriptionSectionProps {
  description?: string;
}

/**
 * Props for ImagesSection component
 */
export interface IImagesSectionProps {
  imageIds: Id<'_storage'>[];
  propertyId: Id<'propertyListings'>;
}

/**
 * Props for ReviewImagesGallery component
 * Displays property images with lightbox preview, drag-drop reordering, and delete functionality
 */
export interface IReviewImagesGalleryProps {
  imageIds: Id<'_storage'>[];
  propertyId: Id<'propertyListings'>;
}

/**
 * Props for SortableReviewImage component (internal to ReviewImagesGallery)
 */
export interface ISortableReviewImageProps {
  id: string;
  storageId: Id<'_storage'>;
  index: number;
  onDelete: () => void;
  onClick: () => void;
}

// ============================================================================
// Property List Page Types
// ============================================================================

/**
 * Filter type for property draft/ready status
 */
export type IPropertyFilter = 'draft' | 'ready';

/**
 * Props for PropertyListPage component
 */
export interface IPropertyListPageProps {
  filter: IPropertyFilter;
}

/**
 * Property list item with video status for display
 */
export interface IPropertyListItem {
  _id: Id<'propertyListings'>;
  _creationTime: number;
  title: string;
  price?: number;
  currency?: string;
  city?: string;
  status: PropertyListingStatus;
  errorMessage?: string;
  imageIds: Id<'_storage'>[];
  videoStatus?: PropertyVideoStatus;
  hasCompletedVideo: boolean;
}

/**
 * Props for PropertyCard component
 */
export interface IPropertyCardProps {
  property: IPropertyListItem;
  onClick: (id: Id<'propertyListings'>) => void;
  /** Whether selection mode is active */
  isSelectionMode?: boolean;
  /** Whether this card is currently selected */
  isSelected?: boolean;
  /** Callback when selection checkbox is toggled */
  onSelectToggle?: (id: Id<'propertyListings'>) => void;
}

/**
 * Props for PropertyStatusBadge component
 */
export interface IPropertyStatusBadgeProps {
  propertyStatus: PropertyListingStatus;
  videoStatus?: PropertyVideoStatus;
  errorMessage?: string;
}

// ============================================================================
// Selection & Bulk Delete Types
// ============================================================================

/**
 * Props for SelectionActionBar component
 */
export interface ISelectionActionBarProps {
  /** Number of selected items */
  selectedCount: number;
  /** Callback when delete button is clicked */
  onDelete: () => void;
  /** Whether a delete operation is in progress */
  isDeleting?: boolean;
}

/**
 * Props for BulkDeleteDialog component
 */
export interface IBulkDeleteDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
  /** Whether delete operation is in progress */
  isDeleting: boolean;
  /** Number of properties to be deleted */
  count: number;
}

// ============================================================================
// Image Lightbox Types
// ============================================================================

/**
 * Props for ImageLightbox component
 */
export interface IImageLightboxProps {
  /** Array of image storage IDs */
  imageIds: Id<'_storage'>[];
  /** Index of the initially displayed image */
  initialIndex: number;
  /** Whether the lightbox is open */
  isOpen: boolean;
  /** Callback to close the lightbox */
  onClose: () => void;
}

// ============================================================================
// Image Processing Types (Step 4)
// ============================================================================

/**
 * Aspect ratio options for image processing
 */
export type IImageAspectRatio = '16:9' | '9:16';

/**
 * Scene type detected by AI for image categorization
 * Note: AI returns 'exterior' which gets mapped to 'building' schema type
 */
export type IImageSceneType =
  | 'exterior' // AI response for building facades (maps to 'building' schema type)
  | 'living_room'
  | 'kitchen'
  | 'bedroom'
  | 'bathroom'
  | 'balcony'
  | 'backyard'
  | 'pool'
  | 'garden'
  | 'parking'
  | 'garage'
  | 'plan' // Floor plan
  | 'other';

/**
 * AI-detected scene type with confidence score
 */
export interface IImageSceneTypeInfo {
  index: number;
  sceneType: IImageSceneType;
  confidence: number;
}

/**
 * Image processing progress tracking
 */
export interface IImageProcessingProgress {
  total: number;
  completed: number;
  status: 'idle' | 'processing' | 'completed' | 'failed';
}

/**
 * Props for ImageProcessingStep component
 */
export interface IImageProcessingStepProps {
  property: IPropertyListing;
  onContinue: (aspectRatio: IImageAspectRatio) => void | Promise<void>;
  onBack: () => void;
  /** Whether the continue action is loading */
  isContinueLoading?: boolean;
}

/**
 * Props for ImageThumbnailGrid component
 * Note: Shows processed images when available, or placeholders when not
 */
export interface IImageThumbnailGridProps {
  /** Original image IDs for reference */
  imageIds: Id<'_storage'>[];
  /** Scene types detected by AI */
  sceneTypes: IImageSceneTypeInfo[];
  /** Indices of excluded images */
  excludedIndices: number[];
  /** Currently selected aspect ratio */
  aspectRatio: IImageAspectRatio;
  /** Processed image IDs for current aspect ratio (if available) */
  processedImageIds?: Id<'_storage'>[];
  /** When true, show skeleton placeholders instead of images */
  showPlaceholders: boolean;
  /** Number of placeholder items to show when no processed images */
  placeholderCount: number;
  /** Callback when an image is clicked */
  onImageClick: (index: number) => void;
}

/**
 * Props for ExcludedImagesSection component
 */
export interface IExcludedImagesSectionProps {
  imageIds: Id<'_storage'>[];
  excludedIndices: number[];
  onRestore: (index: number) => void;
}

/**
 * Props for ImageProcessingControls component
 */
export interface IImageProcessingControlsProps {
  selectedAspectRatio: IImageAspectRatio;
  isProcessing: boolean;
  progress?: IImageProcessingProgress;
  onAspectRatioChange: (ratio: IImageAspectRatio) => void;
  onProcess: () => void;
  onShowOriginalImages: () => void;
}

/**
 * Props for ImagePlaceholderSkeleton component
 */
export interface IImagePlaceholderSkeletonProps {
  aspectRatio: IImageAspectRatio;
  index: number;
}

/**
 * Props for OriginalImagesDialog component
 */
export interface IOriginalImagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageIds: Id<'_storage'>[];
  excludedIndices: number[];
  onExclude: (index: number) => void;
  onRestore: (index: number) => void;
  onReorder: (newOrder: Id<'_storage'>[]) => void;
}

// ============================================================================
// Wizard Navigation Types
// ============================================================================

/**
 * Props for WizardNavigationBar component
 */
export interface IWizardNavigationBarProps {
  currentStep: PropertyVideoWizardStep;
  onBack?: () => void;
  onContinue?: () => void;
  continueLabel?: string;
  backLabel?: string;
  isContinueDisabled?: boolean;
  isContinueLoading?: boolean;
}

/**
 * Props for PropertyVideoPlayer component
 */
export interface IPropertyVideoPlayerProps {
  /** Storage ID of the final video */
  finalVideoId: Id<'_storage'>;
  /** Duration of the video in seconds */
  durationSeconds?: number;
  /** File size in bytes */
  fileSizeBytes?: number;
  /** Property ID for download filename */
  propertyId: Id<'propertyListings'>;
  /** Property title for download filename */
  propertyTitle?: string;
}

// ============================================================================
// Wizard Flow Types
// ============================================================================

/**
 * Wizard flow type - determines which steps to show
 */
export type PropertyWizardFlow = 'new' | 'draft';

/**
 * Step labels for new property flow (6 steps)
 */
export const NEW_PROPERTY_STEP_LABELS = [
  'Enter URL',
  'Review Data',
  'Nearby Places',
  'Process Images',
  'Video Settings',
  'Generation',
] as const;

/**
 * Step labels for draft property flow (4 steps)
 */
export const DRAFT_PROPERTY_STEP_LABELS = [
  'Property Data',
  'Process Images',
  'Video Settings',
  'Generation',
] as const;

/**
 * Props for WizardStepIndicator with dynamic labels
 */
export interface IWizardStepIndicatorDynamicProps {
  currentStep: number;
  totalSteps: number;
  stepLabels: readonly string[];
  onStepClick?: (step: number) => void;
  /** Whether the final step should be clickable (e.g., when video already exists) */
  finalStepClickable?: boolean;
}

// ============================================================================
// PropertyDetailPage Sub-Component Props
// ============================================================================

export interface IPropertyDetailHeaderProps {
  property: IPropertyListing;
  latestVideo?: {
    status: PropertyVideoStatus;
    errorMessage?: string;
  };
  hasCompletedVideo: boolean;
  onBack: () => void;
  formatPrice: (price?: number, currency?: string) => string | null;
}

export interface IPropertyDetailContentProps {
  property: IPropertyListing;
  latestVideo?: {
    status: PropertyVideoStatus;
    finalVideoId?: Id<'_storage'>;
    finalVideoDurationSeconds?: number;
    finalVideoSizeBytes?: number;
  };
  hasCompletedVideo: boolean;
  lightboxOpen: boolean;
  lightboxIndex: number;
  onImageClick: (index: number) => void;
  onLightboxClose: () => void;
}

export interface IPropertyDetailActionsProps {
  property: IPropertyListing;
  hasCompletedVideo: boolean;
  isDeleteDialogOpen: boolean;
  isDeleting: boolean;
  onGenerateVideo: () => void;
  onDeleteClick: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}
